import React, { useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadCloud, Download, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";

export default function ImportClientsDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV or XLSX file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/clients/import?update_existing=${updateExisting}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const good = (data.created || 0) + (data.updated || 0);
      toast.success(`Imported ${good} client${good === 1 ? "" : "s"}`, {
        description: `${data.created} new · ${data.updated} updated · ${data.skipped} skipped`,
      });
      onDone?.();
    } catch (e) {
      toast.error("Import failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const templateUrl = `${API_BASE}/clients/import/template.csv`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl">Import Clients</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Upload a CSV or XLSX exported from Excel / Tally / your CRM. Header names are auto-matched — case, spaces and common synonyms (Client, Business Name, Mobile, GST Number…) all work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border border-dashed border-zinc-300 bg-zinc-50 p-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-600 flex items-center gap-1.5">
                <FileSpreadsheet size={14} /> CSV template
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Columns: name (required), contact_person, email, phone, gstin, pan, categories (GST Monthly; GST Quarterly; ITR; Other), other_services, notes
              </div>
            </div>
            <a
              data-testid="import-template-link"
              href={templateUrl}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-900 hover:underline"
              target="_blank" rel="noreferrer"
            >
              <Download size={13} /> Download
            </a>
          </div>

          <div>
            <input
              ref={inputRef}
              data-testid="import-file-input"
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="rounded-none border-zinc-300 w-full justify-start h-auto py-3"
            >
              <UploadCloud size={16} className="mr-2" />
              <span className="truncate">{file ? file.name : "Choose CSV / XLSX file…"}</span>
            </Button>
            {file && (
              <div className="text-[10px] text-zinc-500 mt-1 tabular">
                {(file.size / 1024).toFixed(1)} KB · {file.type || "auto-detect"}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-700">
            <input
              type="checkbox"
              data-testid="import-update-existing"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
            />
            Update existing clients when email / phone / GSTIN matches (otherwise duplicates are skipped)
          </label>

          {result ? (
            <div className="border border-zinc-200 bg-white p-3 space-y-2" data-testid="import-result">
              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat label="Total rows" value={result.total_rows} />
                <Stat label="Created" value={result.created} tone="good" />
                <Stat label="Updated" value={result.updated} tone="good" />
                <Stat label="Skipped" value={result.skipped} tone={result.skipped ? "warn" : "default"} />
              </div>
              {result.errors?.length ? (
                <div className="mt-2 max-h-40 overflow-y-auto border-t border-zinc-200 pt-2">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500 mb-1 flex items-center gap-1">
                    <AlertCircle size={11} /> {result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped
                  </div>
                  <ul className="text-[11px] text-zinc-600 space-y-0.5">
                    {result.errors.slice(0, 30).map((e, i) => (
                      <li key={i} className="tabular"><span className="font-semibold">Row {e.row}:</span> {e.reason}</li>
                    ))}
                    {result.errors.length > 30 ? <li className="text-zinc-400">… {result.errors.length - 30} more</li> : null}
                  </ul>
                </div>
              ) : (
                <div className="text-[11px] text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> No errors</div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            data-testid="import-submit-btn"
            disabled={busy || !file}
            onClick={submit}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none"
          >
            {busy ? "Importing…" : "Start import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone = "default" }) {
  const c = tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-zinc-900";
  return (
    <div className="border border-zinc-200 p-2">
      <div className={`font-heading font-bold text-lg tabular ${c}`}>{value || 0}</div>
      <div className="text-[9px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">{label}</div>
    </div>
  );
}
